import cx from "clsx";
import SizingContainer from "common-utils/components/SizingContainer";
import { Collection } from "kreate-common/modules/business-types";
import ButtonLink from "kreate-common/modules/kreate-ui/components/ButtonLink";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import Link from "next/link";
import React from "react";
import { MdCopyAll } from "react-icons/md";

import DescriptionViewer from "./components/DescriptionViewer";
import SyncStatusViewer from "./components/SyncStatusViewer";
import Toolbar from "./components/Toolbar";
import styles from "./index.module.scss";

import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { ImageViewer } from "@/modules/kreate-components/components/ImageViewer";
import PageLayout from "@/modules/kreate-components/components/PageLayout";
import UserDisplayNameViewerV2 from "@/modules/kreate-components/components/UserDisplayNameViewerV2";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import { useModalTrigger$ModalAlert } from "@/modules/kreate-modals/modals/ModalAlert/hooks";
import { useModalTrigger$ModalCollectorListViewer } from "@/modules/kreate-modals/modals/ModalCollectorListViewer/hooks";
import { getLinkToPagePolicy } from "@/modules/navigation/utils/getLinkTo";
import { useUser } from "@/modules/resources/hooks/useUser";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  collection: Collection | null | undefined;
};

export default function SectionHead({ className, style, collection }: Props) {
  const { showMessage } = useToast();
  const [collectionOwner] = useUser(collection?.createdBy);

  const handleCopyPolicy = async (policyId: string) => {
    await navigator.clipboard.writeText(policyId);
    showMessage({
      color: "success",
      title: "Copied policy ID to clipboard",
    });
  };

  const modalTriggers = {
    ModalAlert: useModalTrigger$ModalAlert(),
    ModalCollectorListViewer: useModalTrigger$ModalCollectorListViewer(),
  };

  const handleShowPolicyId = async () =>
    await modalTriggers.ModalAlert.openModal(
      <div>
        <Flex.Cell className={styles.fontSerif}>{"Policy IDs: "}</Flex.Cell>
        <ul>
          {collection?.policyIds?.map((policyId) => (
            <li key={policyId}>
              <Flex.Row gap="8px" alignItems="center">
                <Link
                  href={getLinkToPagePolicy(policyId)}
                  className={styles.link}
                >
                  {policyId}
                </Link>
                <ButtonTransparentV2
                  icon={<MdCopyAll />}
                  onClick={async () => await handleCopyPolicy(policyId)}
                />
              </Flex.Row>
            </li>
          ))}
        </ul>
      </div>,
      { modalProps: { size: "lg" } }
    );

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PageLayout.Section>
        <Flex.Row
          justifyContent="space-between"
          gap="12px"
          paddingBottom="20px"
        >
          <Flex.Cell
            className={styles.avatarContainer}
            alignSelf="stretch"
            flex="0 0 auto"
          >
            {collection?.avatar ? (
              <SizingContainer
                className={styles.avatar}
                sizing={{ type: "fixed", width: "160px", height: "160px" }}
              >
                <ImageViewer
                  value={collection?.avatar}
                  sizing="fill"
                  objectFit="cover"
                  sizes="160px"
                />
              </SizingContainer>
            ) : undefined}
          </Flex.Cell>
          <Toolbar collection={collection} />
        </Flex.Row>
        <Flex.Col flex="1 1 560px" gap="16px">
          <h1 className={styles.displayName}>{collection?.title || EM_DASH}</h1>
          <Flex.Col gap="24px">
            <Flex.Row
              alignItems="baseline"
              gap="4px"
              className={styles.createdBy}
            >
              <em>{"By: "}</em>
              <UserDisplayNameViewerV2 user={collectionOwner} unstyled />
            </Flex.Row>
            <Flex.Row gap="20px">
              <ButtonLink
                className={styles.buttonLink}
                title={`${formatInteger$Standard(
                  collection?.numCollectors
                )} Collectors`}
                content={`${formatInteger$Compact(
                  collection?.numCollectors
                )} Collectors`}
                onClick={() =>
                  collection &&
                  void modalTriggers.ModalCollectorListViewer.openModal({
                    collectionId: collection.collectionId,
                  })
                }
                alwaysUnderline
              />
              {collection?.policyIds ? (
                <>
                  <hr className={styles.dashedVerticalDivider} />
                  <ButtonLink
                    className={styles.buttonLink}
                    content={`${collection.policyIds.length} ${
                      collection.policyIds.length === 1
                        ? "Policy ID"
                        : "Policy IDs"
                    }`}
                    alwaysUnderline
                    onClick={handleShowPolicyId}
                  />
                  <hr className={styles.dashedVerticalDivider} />
                  <SyncStatusViewer collection={collection} />
                </>
              ) : null}
            </Flex.Row>
            {collection?.description ? (
              <DescriptionViewer description={collection.description} />
            ) : null}
          </Flex.Col>
        </Flex.Col>
      </PageLayout.Section>
    </div>
  );
}
